import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ArtCard from './ArtCard';
import { apiService } from '../../config/api';
import { useI18n } from '../../i18n';
import SEO, { SEOConfigs } from '../common/SEO';

const slugify = (value = '') => value.toLowerCase().trim().replace(/\s+/g, '-');

const Gallery = () => {
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const location = useLocation();
  const { language, t } = useI18n();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const categoryParam = params.get('category') || 'all';
  const searchQuery = params.get('search') || '';
  const [filter, setFilter] = useState(categoryParam);

  useEffect(() => setFilter(categoryParam), [categoryParam]);

  useEffect(() => {
    let active = true;
    const loadArtworks = async () => {
      try {
        setLoading(true);
        setError(false);
        const response = await apiService.get('/artworks', { limit: 100 });
        if (active) setArtworks(Array.isArray(response?.artworks) ? response.artworks : []);
      } catch (loadError) {
        console.error('Error fetching artworks:', loadError);
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    };
    loadArtworks();
    return () => { active = false; };
  }, []);

  const categories = useMemo(() => ['all', ...new Set(artworks.map((art) => slugify(art.category)).filter(Boolean))], [artworks]);

  const filteredArtworks = useMemo(() => artworks.filter((artwork) => {
    const categoryMatch = filter === 'all' || slugify(artwork.category) === filter;
    const haystack = [artwork.title?.en, artwork.title?.pt, artwork.title?.ar, artwork.description?.en, artwork.description?.pt, artwork.description?.ar, artwork.category]
      .filter(Boolean).join(' ').toLowerCase();
    return categoryMatch && (!searchQuery || haystack.includes(searchQuery.toLowerCase()));
  }), [artworks, filter, searchQuery]);

  const categoryLabel = (category) => {
    if (category === 'all') return t('common.all');
    if (category === 'canvas') return t('common.canvas');
    if (category === 'original') return t('common.original');
    return category.split('-').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="tafsir-page">
      <SEO {...SEOConfigs.gallery} />
      <div className="page-shell">
        <header className="grid gap-8 border-b border-border pb-8 lg:grid-cols-[1fr_auto] lg:items-end">
          <div className="max-w-4xl">
            <p className="editorial-eyebrow mb-3">{t('gallery.eyebrow')}</p>
            <h1 className="editorial-title">{t('gallery.title')}</h1>
            <p className="editorial-lede mt-5 max-w-2xl">
              {searchQuery ? t('gallery.searchResults', { query: searchQuery }) : t('gallery.subtitle')}
            </p>
          </div>

          <div className="flex flex-wrap gap-2" aria-label="Artwork filters">
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                aria-pressed={filter === category}
                onClick={() => setFilter(category)}
                className={`min-h-10 rounded-full border px-4 text-sm transition-colors ${filter === category ? 'border-text-primary bg-text-primary text-surface' : 'border-border text-text-secondary hover:border-primary hover:text-text-primary'}`}
              >
                {categoryLabel(category)}
              </button>
            ))}
          </div>
        </header>

        {loading ? (
          <div className="grid gap-5 py-10 sm:grid-cols-2 lg:grid-cols-3" aria-label={t('common.loading')}>
            {[0, 1, 2, 3, 4, 5].map((item) => <div key={item} className="skeleton-line h-[420px]" />)}
          </div>
        ) : error || filteredArtworks.length === 0 ? (
          <div className="quiet-surface my-10 px-6 py-20 text-center text-text-secondary">{t('gallery.empty')}</div>
        ) : (
          <section className="grid gap-x-5 gap-y-10 py-10 sm:grid-cols-2 lg:grid-cols-3" aria-label={t('gallery.title')}>
            {filteredArtworks.map((art, index) => <ArtCard key={art._id} art={art} index={index} language={language} />)}
          </section>
        )}
      </div>
    </div>
  );
};

export default Gallery;
